/** Counsel-aligned legal copy — single source (Step 10). Update here first. */

export const LEGAL_LAST_UPDATED = "September 23, 2026";

export const PLATFORM_DISCLAIMER =
  "REChecklist and DreamHome Journey are workflow companions for real estate professionals and their clients. They are not a California DRE license, a legal opinion, a compliance certification, or a substitute for your brokerage compliance program.";

export const DRE_DISCLAIMER =
  "License numbers displayed in profiles and footers are recorded by the office. Display does not constitute California DRE certification of this software.";

export const SUPPORTED_SYSTEMS = {
  title: "Supported systems",
  lead: "Desktop backup and export is supported on Windows PC and Apple Mac only.",
  body: [
    "Backup and export tools in this release are designed for Windows PC and Apple Mac desktop browsers (Chrome, Edge, or Safari).",
    "Mobile phones, tablets, Linux, ChromeOS-only workflows, and other operating systems are not supported for file backup or folder-picker export in this build.",
    "You may still use the product for checklist entry where available, but long-term retention requires exporting from a supported desktop.",
  ],
};

export const DATA_RETENTION_DUTY = {
  title: "Your data retention responsibility",
  body: [
    "Your checklist, portfolio, restore points, and browser storage live on this device unless you export them.",
    "Data can be lost if a device is replaced, a browser profile is cleared, storage quota is exceeded, or an account is suspended or closed.",
    "Export JSON, CSV, or PDF copies to a folder you control before those events. We do not recover deleted local data on your behalf.",
    "After account closure we do not retain client checklist content on our servers — your exported copies are your record.",
  ],
};

export const BACKUP_INSTRUCTIONS = {
  title: "Backup instructions",
  steps: [
    "Sign in and open Backup & Export from your Account desk or visit /backup.",
    "Choose JSON for a complete archive, CSV for spreadsheet review, or PDF for a readable summary.",
    "Save to a folder you control — Documents, Desktop, or a cloud-sync folder (OneDrive, iCloud Drive, Dropbox).",
    "Export before cancelling, suspending, replacing a device, or clearing browser storage.",
    "Agents and brokers: also export closed transaction message archives from the messaging desk.",
  ],
};

export const RESTORE_LIMITATIONS = {
  title: "Restore point limitations",
  body: [
    "Restore points are automatic, device-local snapshots for quick rollbacks — not a cloud backup service.",
    "They are capped in number (currently up to 120 per user on this device), deduplicated, and may not capture every keystroke on all platforms.",
    "Restore points are purged when browser storage is cleared or the device is reset.",
    "They are not a substitute for a full export. Always keep an exported backup for long-term retention.",
    "Restoring replaces current data on this device for the selected scope. A safety snapshot is taken first.",
  ],
};

export const SUSPENSION_LIMITATIONS = {
  title: "Suspension and cancellation limitations",
  body: [
    "Suspended accounts may lose write access to desk tools until reactivated. Client portals may pause depending on broker settings.",
    "User-initiated suspension windows and admin suspension may freeze billing while preserving the original billing anchor date.",
    "Cancelled or closed accounts may lose access to desk tools after the grace period. Required transaction records may remain with the office.",
    "Export your data before suspension, cancellation, or device changes. Billing freeze during suspension does not extend your obligation to maintain your own backups.",
    "Reactivation by admin or payment restores desk access when allowed; it does not recreate data you failed to export.",
  ],
};

export const WARRANTY_DISCLAIMER = {
  title: "Warranty and liability disclaimer",
  body: [
    'The Platform is provided "as-is" and "as available," without warranties of any kind, express or implied, including merchantability, fitness for a particular purpose, and non-infringement, to the fullest extent permitted by law.',
    "The operator is not liable for misuse of the Platform; third-party outages (including payment processors and hosting); or decisions made by agents, brokers, buyers, or sellers.",
    "Nothing in the Platform constitutes legal, financial, tax, or brokerage advice. Fair Housing and professional standards remain your responsibility.",
  ],
};

export const EXPORT_FOOTER_DISCLAIMER =
  "Workflow record only — not a California DRE certification, legal filing, or proof of licensed-agency compliance. Supported backup: Windows PC and Apple Mac.";

export const EULA_SECTIONS = [
  {
    id: "license",
    title: "1. License grant",
    paragraphs: [
      "Subject to timely payment of applicable fees and your acceptance of these terms, REChecklist / DreamHome Journey grants you a limited, non-exclusive, non-transferable, revocable license to access and use the Platform for lawful real estate professional or client workflow purposes enabled for your plan.",
      "You may not sublicense, sell, rent, lease, reverse engineer, or create derivative works except as permitted by law. You may not remove proprietary notices or circumvent access controls.",
    ],
  },
  {
    id: "acceptable-use",
    title: "2. Acceptable use",
    paragraphs: [
      "Use the Platform honestly, lawfully, and in a Fair Housing–aligned manner. Describe property and needs — not people as a basis to exclude.",
      "Do not attempt unauthorized access, disrupt service, upload malware, scrape protected content at scale, impersonate others, or use the Platform to harass, defraud, or unlawfully solicit.",
    ],
  },
  {
    id: "account",
    title: "3. Account responsibilities",
    paragraphs: [
      "You are responsible for safeguarding credentials, for activity under your account, and for keeping contact and billing information accurate.",
      "Brokers are responsible for seat assignment and oversight of users under their plan. Agents remain responsible for client data they access.",
    ],
  },
  {
    id: "supported-systems",
    title: "4. Supported systems",
    paragraphs: SUPPORTED_SYSTEMS.body,
  },
  {
    id: "backup",
    title: "5. Backup and export",
    paragraphs: [
      ...BACKUP_INSTRUCTIONS.steps.map((step, index) => `${index + 1}. ${step}`),
      "You are solely responsible for storing, securing, and retaining exported files. Exports are not uploaded to our servers in this local-first build.",
    ],
  },
  {
    id: "restore",
    title: "6. Restore points",
    paragraphs: RESTORE_LIMITATIONS.body,
  },
  {
    id: "suspension",
    title: "7. Suspension and cancellation",
    paragraphs: SUSPENSION_LIMITATIONS.body,
  },
  {
    id: "data-ownership",
    title: "8. Data ownership",
    paragraphs: [
      "Your checklist and desk content belong to you. We do not sell your data for advertising lists.",
      "Client checklist data is for internal servicing of your real estate transaction — not for sale or redistribution.",
      "After closure we do not retain client journey data on our servers. Export before closure.",
    ],
  },
  {
    id: "disclaimer",
    title: "9. Disclaimer",
    paragraphs: [PLATFORM_DISCLAIMER, ...WARRANTY_DISCLAIMER.body],
  },
];

export const TERMS_BILLING = {
  title: "Billing and purchase terms",
  paragraphs: [
    "Paid desk subscriptions bill monthly unless otherwise selected at checkout. No refunds on completed periods.",
    "Cancel anytime; cancel at least 72 hours before renewal to avoid the next charge.",
    "Full purchase terms live on dreamhomejourney.online/purchase-agreement.",
    "Demo accounts are for evaluation. Production billing lives on dreamhomejourney.online.",
  ],
};
